import { type ReactNode, useState } from "react";
import Card from "../UI/Card";
import Text, { type Color } from "../UI/Text";
import Status from "../UI/Status";
import styles from "./TenantSynthese.module.css";
// @ts-expect-error pure js
import useFiles from "../../hooks/use-files";
// @ts-expect-error pure js
import { FileTag } from "../../utils/constants/documents";
// @ts-expect-error pure js
import { FileStatus } from "../../models/documents";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import DocumentsFormUpload from "../File/DocumentsFormUpload";
import DocumentCard from "../File/DocumentCard";
import { IconCheck, IconPlus } from "@tabler/icons-react";
import Button from "../UI/Button.tsx";
import Title from "../UI/Title.tsx";
import type { Tenant } from "../../models/tenant";
import type { DocumentFile } from "../../models/document";
import {
  getDocumentStatus as getDocumentStatusCode,
  type DocumentStatusCode,
} from "../../utils/documentStatus";

interface TenantSyntheseProps {
  tenant: Tenant;
}

interface DocumentStatus {
  color: Color;
  text: string;
  actionLabel: string;
  actionIcon: ReactNode;
}

const STATUS_DISPLAY: Record<
  Exclude<DocumentStatusCode, "valide">,
  DocumentStatus
> = {
  absent: {
    color: "danger",
    text: "Document manquant",
    actionLabel: "Ajouter",
    actionIcon: <IconPlus size={16} />,
  },
  expire: {
    color: "danger",
    text: "Document expiré",
    actionLabel: "Mettre à jour",
    actionIcon: <IconPlus size={16} />,
  },
  aValider: {
    color: "warning",
    text: "À valider",
    actionLabel: "Valider",
    actionIcon: <IconCheck size={16} />,
  },
};

function getDocumentStatus(
  files: DocumentFile[] | undefined,
  tag: string,
): DocumentStatus {
  const status = getDocumentStatusCode(files, tag);
  if (status === "valide") {
    return {
      color: "success",
      text: tag === FileTag.ASSURANCE_HABITATION ? "Document valide" : "Signé",
      actionLabel: "Voir",
      actionIcon: <IconCheck size={16} />,
    };
  }
  return STATUS_DISPLAY[status];
}

interface SyntheseRowProps {
  label: string;
  status: DocumentStatus;
  onAction: () => void;
  requiresWrite?: boolean;
}

function SyntheseRow({
  label,
  status,
  onAction,
  requiresWrite,
}: SyntheseRowProps) {
  return (
    <div className={styles.row}>
      <div>
        <Text variant="deci" weight="medium" className={styles.rowText}>
          {label}
        </Text>
        <Status color={status.color} text={status.text} />
      </div>
      <Button
        variant="secondary"
        onClick={onAction}
        requiresWrite={requiresWrite}
      >
        {status.actionLabel}
        {status.actionIcon}
      </Button>
    </div>
  );
}

export default function TenantSynthese({ tenant }: TenantSyntheseProps) {
  const { files, deleteFile, updateFile } = useFiles({ tenantId: tenant?.id });
  const [DocumentModal, closeDocumentModal, openDocumentModal] = useModal();
  const [currentTag, setCurrentTag] = useState<string>("");

  const documentTypes = [
    { tag: FileTag.ASSURANCE_HABITATION, label: "Assurance multirisque" },
    { tag: FileTag.BAIL, label: "Bail" },
    { tag: FileTag.EDL, label: "État des lieux" },
  ];

  function handleAction(tag: string) {
    setCurrentTag(tag);
    openDocumentModal();
  }

  async function deleteDocumentHandler(fileId: number) {
    try {
      await deleteFile(fileId);
    } catch (error) {
      console.error(error);
    }
  }

  const currentFiles =
    files?.filter((file: DocumentFile) => file.tag === currentTag) || [];

  // Pour un fichier déjà validé/signé, on affiche la DocumentCard (qui sert
  // bien la version signée) plutôt que le formulaire d'édition, dont l'aperçu
  // déprécié ne récupère que la version non signée.
  const showFileCards =
    currentFiles.length > 1 ||
    (currentFiles.length === 1 &&
      currentFiles[0].status === FileStatus.valide.status);

  return (
    <>
      <DocumentModal size="big">
        {showFileCards ? (
          <div className={styles.documentGrid}>
            {currentFiles.map((file: DocumentFile) => (
              <DocumentCard
                key={file.id}
                titre={file.filename ?? ""}
                files={[file]}
                deleteDocumentHandler={deleteDocumentHandler}
                isBailleur
                tenant={tenant}
                updateFile={updateFile}
                allowEdit
              />
            ))}
          </div>
        ) : (
          <DocumentsFormUpload
            tenant={tenant}
            onSubmitted={closeDocumentModal}
            isBailleur
            typeDocument={currentTag}
            file={currentFiles[0]}
          />
        )}
      </DocumentModal>

      <Card>
        <Title variant="deca" weight="bold" className={styles.title}>
          Ma synthèse
        </Title>
        <div>
          {documentTypes.map(({ tag, label }) => {
            const docStatus = getDocumentStatus(files, tag);
            const isWriteAction = docStatus.actionLabel !== "Voir";
            return (
              <SyntheseRow
                key={tag}
                label={label}
                status={docStatus}
                onAction={() => handleAction(tag)}
                requiresWrite={isWriteAction}
              />
            );
          })}
        </div>
      </Card>
    </>
  );
}
